import config from '../config';
import { privateAutomaticProperties, sanitizeAnalyticsProperties, sanitizeAnalyticsPayload } from './analyticsPrivacy';

let mixpanelInstance = null;
let initPromise = null;
let sessionId = null;
const pending = [];
const SESSION_KEY = 'spotlight_analytics_session_v1';

const warn = () => {
  // Never include SDK error text or event payloads in diagnostics.
  // eslint-disable-next-line no-console
  console.warn('Spotlight analytics operation failed');
};

const getSessionId = (rotate = false) => {
  if (sessionId && !rotate) return sessionId;
  try {
    if (!rotate) sessionId = window.sessionStorage.getItem(SESSION_KEY);
  } catch { /* Analytics must tolerate unavailable storage. */ }
  if (!sessionId || rotate) {
    sessionId = window.crypto?.randomUUID?.()
      || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    try {
      window.sessionStorage.setItem(SESSION_KEY, sessionId);
    } catch { /* Keep an in-memory session when storage is unavailable. */ }
  }
  return sessionId;
};

const commonProperties = () => ({
  environment: config.environment || 'unknown',
  platform: 'web',
  app_version: config.version || 'unknown',
  build_number: config.version || 'unknown',
  session_id: getSessionId(),
  schema_version: 1,
  source: 'client',
});

const execute = (operation, resolve) => {
  try {
    resolve(operation(mixpanelInstance));
  } catch {
    warn();
    resolve(false);
  }
};

export function initMixpanel() {
  if (initPromise) return initPromise;
  if (!config.mixpanelToken || typeof window === 'undefined') return null;

  initPromise = import('mixpanel-browser').then((module) => {
    const instance = module.default || module;
    instance.init(config.mixpanelToken, {
      ignore_dnt: true,
      autocapture: false,
      track_pageview: false,
      record_sessions_percent: 0,
      property_blacklist: privateAutomaticProperties,
      hooks: { before_send_events: sanitizeAnalyticsPayload },
    });
    instance.register(commonProperties());
    mixpanelInstance = instance;
    // Enqueued identity changes and events retain their original call order.
    pending.splice(0).forEach(({ operation, resolve }) => execute(operation, resolve));
    return instance;
  }).catch(() => {
    initPromise = null;
    mixpanelInstance = null;
    pending.splice(0).forEach(({ resolve }) => resolve(false));
    warn();
    return null;
  });
  return initPromise;
}

const enqueue = (operation) => new Promise((resolve) => {
  if (mixpanelInstance) {
    // Keep sendBeacon synchronous when a redirect immediately follows a click.
    execute(operation, resolve);
    return;
  }
  pending.push({ operation, resolve });
  if (!initMixpanel()) pending.splice(0).forEach((item) => item.resolve(false));
});

export const setAnalyticsUser = (user) => {
  // Existing UI distinguishes guest users by the absence of an email.
  // Email is used only for that decision and is never passed to Mixpanel.
  const isAccount = user?.email && user?.guest !== true
    && user?.['registered?'] !== false && user?.registered !== false;
  const id = isAccount && user?.id != null ? String(user.id) : null;
  return enqueue((mixpanel) => {
    const previousId = mixpanel.get_property('$user_id');
    if (String(previousId || '') !== String(id || '')) {
      // Flush active reader intervals under the old identity, before reset/identify.
      window.dispatchEvent(new Event('spotlight:analytics-identity-changing'));
    }
    if (previousId && String(previousId) !== id) {
      mixpanel.reset();
      getSessionId(true);
    }
    if (id && String(previousId || '') !== id) mixpanel.identify(id);
    mixpanel.register({ ...commonProperties(), auth_state: id ? 'authenticated' : 'anonymous' });
    return true;
  });
};

export function track(eventName, props = {}) {
  const snapshot = { ...sanitizeAnalyticsProperties(props) };
  return enqueue((mixpanel) => Boolean(mixpanel.track(eventName, snapshot)));
}

export function trackImmediately(eventName, props = {}) {
  const snapshot = { ...sanitizeAnalyticsProperties(props) };
  return enqueue((mixpanel) => Boolean(mixpanel.track(eventName, snapshot, {
    transport: 'sendBeacon',
    send_immediately: true,
  })));
}

// Preserve the release branch's contest attribution API.
export async function getMixpanelDistinctId() {
  const instance = mixpanelInstance || await initMixpanel();
  if (!instance || typeof instance.get_distinct_id !== 'function') return null;
  try { return instance.get_distinct_id() || null; } catch { return null; }
}
